import React, { type FC, type PropsWithChildren } from 'react';

import { useInView } from 'react-intersection-observer';
import { getColor } from '@lucid/theme';
// import reactHTMLParser from 'html-react-parser';
import { useTheme } from '@emotion/react';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import CtaWithSSO from '../cta-with-sso/cta-with-sso';
import Heading from '../heading/heading';
import Link from '../link/link';
import Text from '../text/text';
import { MediaCarousel } from '..';
import type { ComponentLink } from '../../types/generics';

export interface HeroCarouselProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The link to be displayed in the CarouselSlider component. */
  cta?: ComponentLink;
  /** The header text for the CarouselSlider component. */
  header?: string | string[];
  /** The i18n translation data. */
  /** Should include in query:
   ** mktg_ct_add_on_pause_animation_label
   ** mktg_ct_add_on_play_animation_label
   ** mktg_competitor_pause_slideshow
   ** mktg_carousel_slide_previous
   ** mktg_carousel_slide_next
   */
  i18nData?: Lucid.I18n.DataEdges;
  /** The id to use for aria purposes */
  id?: string;
  /** The list of items that correspond to each CarouselSlider item. */
  items: Lucid.Directus.Generated.Carousel_Item[];
  /** Boolean for if header should be below the imagery or above.  */
  hasLowerHeader?: boolean;
  /** Will render CTA with SSO component */
  hasSSO?: boolean;
  /** Sets the time to change the CarouselSlider image. @default 7000 = 4s */
  sliderTime?: number;
  /** The subheader text for the CarouselSlider component. */
  subheader?: string;
  /** Translated aria label for carousel section */
  ariaLabel?: string;
  /** Specify the heading color */
  headingColor?: string;
  /** Specify the subheading color */
  subheadingColor?: string;
  /** Specify the hero's background color */
  heroBackgroundColor?: string;
}

const HeroCarousel: FC<PropsWithChildren<HeroCarouselProps>> = ({
  ariaLabel = '',
  className,
  cta,
  i18nData,
  id = 'hero-carousel-items',
  items = [],
  header,
  sliderTime = 7000,
  subheader,
  subheadingColor = 'coolGrey.100',
  hasLowerHeader = false,
  hasSSO,
  headingColor,
  heroBackgroundColor = 'coolGrey.5',
  ...rest
}) => {
  const carouselItems = items || [];
  const [inViewRef, isInView] = useInView({ threshold: 0.3 });

  const theme = useTheme();
  const updatedBackgroundColor = getColor({
    color: heroBackgroundColor,
    theme,
  });
  const updatedSubheadingColor = getColor({
    color: subheadingColor,
    theme,
  });

  const hasCarouselItems = carouselItems.length > 0;

  return (
    <Box ref={inViewRef} {...rest} className={className} pb={hasCarouselItems ? 7 : 0}>
      {header && (
        <Box
          css={theme => ({
            background: updatedBackgroundColor,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: theme.space.s40,
          })}
        >
          <Heading
            as="h1"
            color={headingColor}
            css={{ maxWidth: '780px' }}
            dataElementPageLocation="hero"
            variant="hero2XL700"
          >
            {header}
          </Heading>
          <Text
            css={theme => ({ maxWidth: '780px', marginBottom: theme.space.s24, color: updatedSubheadingColor })}
            dataElementPageLocation="hero"
            variant="paragraphM400"
          >
            {subheader}
          </Text>
          {hasSSO && <CtaWithSSO i18nData={i18nData} />}
          {cta?.final_url && !hasSSO && (
            <Link displayAsButton href={cta?.final_url} size="medium">
              {cta?.default_link_text}
            </Link>
          )}
        </Box>
      )}
      {hasCarouselItems && (
        <MediaCarousel
          ariaLabel={ariaLabel}
          hasLowerHeader={hasLowerHeader}
          i18nData={i18nData}
          id={id}
          isInView={isInView}
          items={carouselItems}
          sliderTime={sliderTime}
        />
      )}
    </Box>
  );
};

export default HeroCarousel;
